import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import {
  useIsMobile,
  useIsPastMobileOverlayCutover,
  useIsTablet,
} from "./useIsMobile";

/**
 * Persistent-sidebar widths shared by every portal shell. Expanded shows full
 * labels; "mini" is the icon-only rail — collapsing shrinks to this instead of
 * hiding the navbar, so nav stays one click away.
 */
export const NAV_WIDTH_EXPANDED = 220;
export const NAV_WIDTH_MINI = 72;

export interface NavbarCollapse {
  /** Mobile (< sm) burger-overlay open state. */
  mobileOpened: boolean;
  toggleMobile: () => void;
  /** Desktop/tablet sidebar expanded (vs. collapsed-to-rail). */
  sidebarOpened: boolean;
  toggleSidebar: () => void;
  /**
   * Collapse after picking a nav item — only acts in the space-constrained
   * tablet band (no-op elsewhere). Wire onto each NavLink's `onClick`.
   */
  collapseAfterNav: () => void;
  /**
   * True when the persistent sidebar should render as the icon-only rail
   * (>= sm and collapsed). Never true for the < sm burger overlay, which
   * always shows full labels.
   */
  mini: boolean;
  /** Current navbar width — feeds AppShell `navbar.width` and SidebarToggle. */
  navWidth: number;
}

/**
 * Shared collapse/expand state for the three portal shells (app, admin,
 * auditor). Collapsing shrinks the persistent sidebar to a 72px icon rail
 * rather than hiding it, so nav stays reachable in every portal. The
 * expanded/collapsed preference persists across sessions — and across portals,
 * since they share one `navbar-desktop-opened` key (so the rail state carries
 * coherently when moving between the app and admin/auditor portals).
 *
 * @param tablet Enable the iPad (sm..lg, portrait AND landscape) band that
 *   defaults the sidebar collapsed and re-collapses it after each nav
 *   selection. Only the app portal opts in; admin/auditor keep the sidebar
 *   persistent on tablets.
 */
export function useNavbarCollapse({
  tablet = false,
}: { tablet?: boolean } = {}): NavbarCollapse {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] =
    useDisclosure();
  const [desktopOpened, setDesktopOpened] = useLocalStorage({
    key: "navbar-desktop-opened",
    defaultValue: true,
    // Read synchronously on first render (no SSR here) — the default reads in
    // an effect and causes a visible open→collapsed layout jump.
    getInitialValueInEffect: false,
  });

  // The tablet band (sm..lg) sits past the navbar breakpoint, so an expanded
  // sidebar would eat ~220px of an 834–1194px screen. Default it collapsed
  // there, session-only, leaving the persisted desktop preference untouched.
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  // Session-only, and DELIBERATELY not reset by rotation or a `isTablet`
  // transition: a user who expands the rail is expressing a preference, the
  // same way `desktopOpened` (above) persists until explicitly toggled again.
  // Verified this survives an in-band rotation (e.g. 834<->1194, both inside
  // sm..lg) by design, not by omission — the one thing that DOES need to
  // reset on leaving the mobile band is `mobileOpened` (the burger overlay),
  // handled by its own effect below; that is a transient overlay state, not a
  // stated preference, and its own bug (the full-screen nav takeover
  // reappearing) was the reason this file grew a rotation-safety effect at
  // all. Reset only where the correct behavior is a reset.
  const [tabletOpened, setTabletOpened] = useState(false);
  const tabletActive = tablet && isTablet;

  const location = useLocation();
  // The mobile navbar is a burger-triggered overlay; close it on route change
  // so tapping a nav item reveals the page instead of leaving the overlay over
  // it. No-op on desktop (that uses `desktopOpened`).
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  // Rotation safety. The burger overlay only exists below `sm`; above it the
  // navbar is persistent and `mobileOpened` is not read. But the flag SURVIVED
  // the trip, so an iPad that opened the overlay in portrait, rotated to
  // landscape and rotated back found the full-screen panel re-asserted over
  // the page without anyone touching it — measured portrait -> landscape ->
  // portrait, the navbar came back at x=0, w=834. Close it on the way out of
  // the band so returning to it is always a clean state.
  //
  // Gated on `useIsPastMobileOverlayCutover`, NOT `isMobile` — Mantine's own
  // AppShell flips its `collapsed.mobile` CSS 0.1px earlier than `isMobile`'s
  // plain `max-width: sm` (768px inclusive), while a SEPARATE rule forces
  // `width: 100%` under the RAW 768px breakpoint whenever `mobileOpened` is
  // still `true`. Gating this close on `isMobile` closed the flag one hair
  // too late: at EXACTLY 768px, `isMobile` was still `true` (768 <= 768), so
  // a burger opened below 768 and then resized live to precisely 768 without
  // a reload reproduced the full-screen takeover a fresh load at that same
  // width never shows — measured `navbar width: 768px (100%)` versus a fresh
  // load's `220px`. See useIsPastMobileOverlayCutover's own doc comment for
  // the full mechanism.
  const isPastMobileOverlayCutover = useIsPastMobileOverlayCutover();
  useEffect(() => {
    if (isPastMobileOverlayCutover) closeMobile();
  }, [isPastMobileOverlayCutover, closeMobile]);

  const sidebarOpened = tabletActive ? tabletOpened : desktopOpened;
  const toggleSidebar = tabletActive
    ? () => setTabletOpened((o) => !o)
    : () => setDesktopOpened((o) => !o);
  // Handled at the click site rather than a route effect
  // (react-hooks/set-state-in-effect); no-op unless the tablet band is
  // active — was checking `tablet` (the static opt-in flag, always true for
  // the app portal) instead of `tabletActive` (SCRUM-163: harmless today
  // since the read side already gates on tabletActive, but it meant this
  // fired — and set tabletOpened false — on every nav click at every
  // viewport width, not just the intended sm..lg band).
  const collapseAfterNav = () => {
    if (tabletActive) setTabletOpened(false);
  };

  // `mini` must follow the NAVBAR breakpoint (`sm`), not `md`. Below `sm` the
  // navbar is the burger overlay, which always shows full labels — a rail
  // there would be a rail nobody can reach. At or above it the navbar is
  // persistent, so collapsing means the 72px rail. Keying this off `md`
  // (useIsCompact) gave 768–991px — an 11" iPad in portrait — a persistent but
  // fully EXPANDED 220px sidebar, a quarter of the screen.
  const mini = !isMobile && !sidebarOpened;
  const navWidth = mini ? NAV_WIDTH_MINI : NAV_WIDTH_EXPANDED;

  return {
    mobileOpened,
    toggleMobile,
    sidebarOpened,
    toggleSidebar,
    collapseAfterNav,
    mini,
    navWidth,
  };
}
